// @ts-nocheck
import React from 'react';
import KeyValue from './KeyValue';
import KeyProvider from './KeyProvider';
import Platform from './Platform';
import PackageId from './PackageId';
import KeyCount from './KeyCount';
import KebabMenu from '../../common/KebabMenu';
import GameLink from '../../common/links/GameLink';
import Button from '../../common/Button';
import icons from '../../../constants/icons';
import { RequestStatusLabel } from './RequestStatusLabel';
import { useTranslation } from 'react-i18next';
import { isPowerUser } from '../../auth/authUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface GamesCompressedViewProps {
  requests: any[];
  canWithdrawRequest: (request: any) => boolean;
  processing: boolean;
  onWithdrawRequest: (request: any) => void;
  canSetStatus: (request: any) => boolean;
  onSetStatus: (request: any) => void;
  canDownloadKeys: (request: any) => boolean;
  onDownloadKeys: (request: any) => void;
  canRequestOnSteam: (request: any) => boolean;
  onSteamRequest: (request: any) => void;
  canUploadKeys: (request: any) => boolean;
  onUploadKeys: (request: any) => void;
  canSetKeysOrdered: (request: any) => boolean;
  onKeysOrdered: (request: any) => void;
  onShowDetails: (request: any) => void;
  onApproveItem: (request: any) => void;
  onDeclineItem: (request: any) => void;
  onEditRequestKeyCount: (request: any) => void;
  onEditRequestValue: (request: any) => void;
  canDisableKeys: (request: any) => boolean;
  onDisableKeys: (request: any) => void;
  canAssignRequest: (request: any) => boolean;
  onReassign: (request: any) => void;
  showAllButtons: boolean;
  endUserDetailsRequired: boolean;
  handleUploadAccountDetails: (request: any) => void;
  canReturnKeys: (request: any) => boolean;
  handleReturnKeys: (request: any) => void;
  currentUser: Record<string, any> | null;
}

function GamesCompressedView({
  requests,
  canWithdrawRequest,
  processing,
  onWithdrawRequest,
  canSetStatus,
  onSetStatus,
  canDownloadKeys,
  onDownloadKeys,
  canRequestOnSteam,
  onSteamRequest,
  canUploadKeys,
  onUploadKeys,
  canSetKeysOrdered,
  onKeysOrdered,
  onShowDetails,
  onApproveItem,
  onDeclineItem,
  onEditRequestKeyCount,
  onEditRequestValue,
  canDisableKeys,
  onDisableKeys,
  canAssignRequest,
  onReassign,
  showAllButtons,
  endUserDetailsRequired,
  handleUploadAccountDetails,
  canReturnKeys,
  handleReturnKeys,
  currentUser,
}: GamesCompressedViewProps) {
  const { t } = useTranslation();

  const requestRequiresAccountDetails = (r) => {
    const endUserDetailsRequiredForRequest =
      r.endUserDetailsRequired ?? r.EndUserDetailsRequired;

    const isDownloadValue =
      r.isKeyDeliveryMethodDownload ?? r.IsKeyDeliveryMethodDownload;

    const isDownload =
      isDownloadValue === true ||
      isDownloadValue === 1 ||
      (typeof isDownloadValue === 'string' &&
        (isDownloadValue.trim().toLowerCase() === 'true' ||
          isDownloadValue.trim().toLowerCase() === '1'));

    // Prefer explicit per-request flag if present; fall back to delivery method.
    return endUserDetailsRequiredForRequest === true || isDownload;
  };

  return (
    <table className="table table-has-buttons width-100">
      <thead>
        <tr>
          <th className="no-wrap" style={{ minWidth: '100px', width: '100px' }}>
            #
          </th>
          <th>{t('Status')}</th>
          <th>{t('Game')}</th>
          <th className="text-right num no-wrap">{t('Keys')}</th>
          <th className="text-right num no-wrap">{t('Value')}</th>
          <th>{t('Key provider')}</th>
          <th>{t('Platform')}</th>
          <th>{t('Package Id')}</th>
          {isPowerUser(currentUser) && <th>{t('Assigned to')}</th>}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {requests?.map((request) => (
          <tr key={request.id} className="edit-hover">
            <td>{request.reference}</td>
            <td>
              <RequestStatusLabel
                requestStatus={request.requestStatus}
                isWaitingForKeys={request.isWaitingForKeys}
                isUpdateInProgress={request.isUpdateInProgress}
                isDownloaded={request.downloaded}
                isEmailSent={request.allAccountDetailsEmailsSent}
              />
            </td>
            <td>
              <GameLink
                gameId={request.gameId}
                skuId={request.skuId}
                gameTitle={request.gameTitle}
                skuName={request.skuName}
                key={request.id}
                currentUser={currentUser}
              />
            </td>
            <td className="text-right num no-wrap">
              <KeyCount
                request={request}
                processing={processing}
                onEditRequestKeyCount={onEditRequestKeyCount}
                compressed={true}
              ></KeyCount>
            </td>
            <td className="text-right num no-wrap">
              <KeyValue
                request={request}
                processing={processing}
                onEditRequestValue={onEditRequestValue}
                compressed={true}
              ></KeyValue>
            </td>
            <td>
              <KeyProvider request={request} compressed={true}></KeyProvider>
            </td>
            <td>
              <Platform request={request} compressed={true}></Platform>
            </td>
            <td>
              <PackageId
                request={request}
                currentUser={currentUser}
                compressed={true}
              ></PackageId>
            </td>
            {isPowerUser(currentUser) && (
              <td>
                <div
                  tabIndex={0}
                  disabled={processing}
                  onClick={(e) => onReassign(e, request)}
                  className="a"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onReassign(e, request);
                    }
                  }}
                >
                  <span style={{ fontSize: '12px' }}>
                    <FontAwesomeIcon icon={icons.REASSIGN} className="me-3" />
                  </span>
                  <span className="edit-hover-item">
                    {request.assignedToUserProfileFullName || t('Unassigned')}
                  </span>
                </div>
              </td>
            )}
            <td className="text-right no-wrap">
              <div className="d-inline-flex">
                <KebabMenu
                  skip={1}
                  take={1}
                  reverse={true}
                  className="inline-icons"
                  buttons={[
                    canWithdrawRequest(request) && (
                      <Button
                        testId="withdraw-request-button"
                        isDisabled={processing}
                        className="btn btn-danger btn-xs-full me-2"
                        dropdownclasses="danger"
                        onClick={(e) => onWithdrawRequest(e, request)}
                        icon={icons.WITHDRAW_REQUEST}
                        text={t('Withdraw request')}
                      />
                    ),
                    canDisableKeys(request) && (
                      <Button
                        isDisabled={processing}
                        className="btn btn-danger btn-xs-full me-2"
                        dropdownclasses="danger"
                        onClick={(e) => onDisableKeys(e, request)}
                        icon={icons.DISABLE_KEYS}
                        text={t('Disable keys')}
                      />
                    ),
                    canAssignRequest(request) && (
                      <Button
                        isDisabled={processing}
                        className="btn btn-default me-2"
                        onClick={(e) => onReassign(e, request)}
                        icon={icons.REASSIGN}
                        text={t('Reassign')}
                        title={t('Reassign the request to a user')}
                      ></Button>
                    ),
                    canReturnKeys(request) && (
                      <Button
                        isDisabled={processing}
                        className="btn btn-default me-2"
                        onClick={(e) => handleReturnKeys(e, request)}
                        icon={icons.RETURN_KEYS}
                        text={t('Return keys')}
                        title={t('Return keys back to key pool')}
                      ></Button>
                    ),

                    canSetStatus(request) && (
                      <Button
                        isDisabled={processing}
                        className="btn btn-default btn-xs-full me-2"
                        onClick={(e) => onSetStatus(e, request)}
                        icon={icons.SET_STATUS}
                        text={t('Set status')}
                      />
                    ),

                    canDownloadKeys(request) && (
                      <Button
                        isDisabled={processing}
                        className="btn btn-default btn-xs-full me-2"
                        onClick={(e) => onDownloadKeys(e, request)}
                        icon={icons.DOWNLOAD_KEYS}
                        text={t('Download keys')}
                      />
                    ),

                    canRequestOnSteam(request) && (
                      <Button
                        isDisabled={processing}
                        className="btn btn-default btn-xs-full me-2"
                        onClick={(e) => onSteamRequest(e, request)}
                        icon={icons.REQUEST_ON_STEAM}
                        text={t('Request on Steam')}
                      />
                    ),
                    canSetKeysOrdered(request) && (
                      <Button
                        isDisabled={processing}
                        className="btn btn-default btn-xs-full me-2"
                        onClick={(e) => onKeysOrdered(e, request)}
                        icon={icons.SET_KEYS_ORDERED}
                        text={t('Keys ordered')}
                      />
                    ),
                    requestRequiresAccountDetails(request) && (
                      <Button
                        className="btn btn-default btn-xs-full me-2"
                        onClick={() =>
                          handleUploadAccountDetails(
                            request.id,
                            request.numberOfKeys
                          )
                        }
                        icon={icons.UPLOAD_ACCOUNT_DETAILS}
                        text={t('Upload account details')}
                      />
                    ),
                    canUploadKeys(request) && (
                      <Button
                        isDisabled={processing}
                        className="btn btn-default btn-xs-full me-2"
                        onClick={(e) => onUploadKeys(e, request)}
                        icon={icons.UPLOAD_KEYS}
                        text={t('Upload keys')}
                      />
                    ),
                    <Button
                      isDisabled={processing}
                      className="btn btn-default btn-xs-full"
                      onClick={(e) => onShowDetails(e, request)}
                      icon={icons.INFO}
                      text={t('Details')}
                    />,
                  ]}
                ></KebabMenu>
                {request.currentUserCanSign && (
                  <>
                    <Button
                      isDisabled={processing}
                      title={processing ? t('Processing') : t('Approve')}
                      onClick={(e) => onApproveItem(e, request)}
                      className={`btn btn-default btn--approval r-corners-left ${request.action === 'approve' ? 'active' : ''
                        }`}
                      icon={icons.APPROVE}
                      text={t('Approve')}
                      hideTitle={true}
                    />
                    <Button
                      isDisabled={processing}
                      title={processing ? t('Processing') : t('Decline')}
                      onClick={(e) => onDeclineItem(e, request)}
                      className={`btn btn-default btn--decline r-corners-right me-2 ${request.action === 'decline' ? 'active' : ''
                        }`}
                      icon={icons.DECLINE}
                      text={t('Decline')}
                      hideTitle={true}
                    />
                  </>
                )}
                {request.currentUserHasSigned && (
                  <span className="badge bg-success r-corners">
                    {request.signedDate}
                  </span>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default GamesCompressedView;
